import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, FlexBox, Button, Typography, Grid } from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import DashboardView from '../DashboardView';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { actions as authActions, selectors as authSelector } from 'services/auth';
import { actions as kioskActions, selectors as kioskSelectors } from 'services/kiosk';
import { actions as ticketingActions } from 'services/ticketing';
import { actions as salesErrorActions } from 'services/salesErrors';
import { actions as hardwareActions, selectors as hardwareSelectors } from 'services/hardware';
import { actions as maintenanceActions, selectors as maintenanceSelectors } from 'services/maintenance';
import packageJSON from '../../../../package.json';
import { TimerOverlay } from 'components/TimerOverlay';
import { TicketStockOverlay } from 'components/TicketStockOverlay';
import { selectors as configSelectors } from 'services/configuration';
import { useStyles } from './styled';
import get from 'lodash.get';

const MaintenanceView: React.FC = () => {
  const { i18n } = useTranslation();

  const [activeTab, setActiveTab] = React.useState<string>('kiosk');
  const [showTimerOverlay, setShowTimerOverlay] = React.useState(false);
  const [showConfirmationOverlay, setShowConfirmationOverlay] = React.useState(false);
  const [showTicketStock, setShowTicketStock] = React.useState(false);
  const [showRestockOverlay, setShowRestockOverlay] = React.useState(false);
  const classes = useStyles();

  const navigate = useNavigate();
  const maintenanceToken = useSelector(authSelector.selectMaintenanceId);
  const config = useSelector(configSelectors.selectConfiguration);
  const anyHardwareErrors = useSelector(hardwareSelectors.selectAnyHardwareErrors);
  const lowStockThresholdReached = useSelector(kioskSelectors.selectLowStockThresholdReached);
  const kioskHardwareIdOutOfStock = useSelector(maintenanceSelectors.selectKioskHardwareIdOutOfStock);

  const { setKioskHardwareIdOutOfStock, resetMaintenance } = useActions(maintenanceActions);
  const { removeMaintenanceToken, resetTicketing, resetSalesError, checkLowStockThresholds, setPrintData } = useActions(
    {
      removeMaintenanceToken: authActions.removeMaintenanceToken,
      resetTicketing: ticketingActions.resetTicketing,
      resetSalesError: salesErrorActions.resetError,
      checkLowStockThresholds: kioskActions.checkLowStockThresholds,
      setPrintData: hardwareActions.setPrintData
    }
  );
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const maintenanceTimeout = get(config, 'configuration.maintenanceTimeout', 30);
  const maintenanceTimerCount = get(config, 'configuration.maintenanceTimerCount', 10);

  React.useEffect(() => {
    document.addEventListener('click', handleInactivityTimer, true);
    handleInactivityTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  React.useEffect(() => {
    setPrintData({ printJobs: [] });
    // TODO: remove this when localizing the maintenance view
    // currently preventing the dates from being the only localized text
    i18n.changeLanguage('enUS');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
  };

  const handleInactivityTimer = () => {
    resetTimer();
    timer.current = setTimeout(() => {
      setShowTimerOverlay(true);
    }, maintenanceTimeout * 1000);
  };

  const handleActiveClick = () => {
    handleInactivityTimer();
    setShowTimerOverlay(false);
  };

  const exitToAttract = () => {
    removeMaintenanceToken();
    resetTicketing();
    resetSalesError();
    resetMaintenance();
    checkLowStockThresholds();
    localStorage.removeItem('maintenance_token');
    document.removeEventListener('click', handleInactivityTimer, true);
    navigate('/ticketing/attract');
  };

  const logoutButtonClick = () => {
    if (anyHardwareErrors || lowStockThresholdReached) {
      setShowConfirmationOverlay(true);
    } else if (kioskHardwareIdOutOfStock) {
      setShowRestockOverlay(true);
    } else {
      exitToAttract();
    }
  };

  const onTicketRestockConfirm = () => {
    setKioskHardwareIdOutOfStock(undefined);
    setShowTicketStock(false);
  };

  if (!maintenanceToken) exitToAttract();

  return (
    <>
      {showRestockOverlay && (
        <ActionOverlay
          title={
            <Typography variant="h2" align="center">
              {"Don't forget to update the stock count"}
            </Typography>
          }
          height={660}
          description={
            <Box width={600}>
              <Typography align="center" fontSize={30}>
                We detected that you restocked the printer. Do you want to update the stock count?
              </Typography>
            </Box>
          }
          confirmText="Yes"
          onConfirm={() => {
            setShowRestockOverlay(false);
            setShowTicketStock(true);
          }}
          cancelText="No"
          onCancel={() => {
            setKioskHardwareIdOutOfStock(undefined);
            setShowRestockOverlay(false);
            exitToAttract();
          }}
        />
      )}
      {showConfirmationOverlay && (
        <ActionOverlay
          title={
            <Typography variant="h2" align="center">
              Are you sure you want to leave?
            </Typography>
          }
          height={660}
          description={
            <Box width={600}>
              <Typography align="center" fontSize={30}>
                It looks like there are still some errors with the hardware. If left unresolved, the kiosk will remain
                inactive.
              </Typography>
            </Box>
          }
          confirmText="Logout"
          onConfirm={exitToAttract}
          cancelText="Back"
          onCancel={() => setShowConfirmationOverlay(false)}
        />
      )}
      <Grid container direction="row" className={classes.maintenanceOuterContainer}>
        <Grid container item direction="column" className={classes.maintenanceTabContainer}>
          <Tabs size="large" value={activeTab} onChange={(e, v) => setActiveTab(v as string)}>
            <Tabs.Tab label="Kiosk" value="kiosk" iconName="kiosks" />
          </Tabs>
          <FlexBox direction="column" margin="0 0 8">
            <Button data-testid="exitbutton" sx={{ margin: '0 22px 18px' }} onClick={logoutButtonClick}>
              Logout
            </Button>
            <Typography variant="body1" align="center" data-testid="versionNumber">
              Version: {packageJSON.version}
            </Typography>
            <Typography variant="body1" align="center">
              &#169;KIS Technologies
            </Typography>
          </FlexBox>
        </Grid>
        <Grid direction="row" className={classes.maintenancePortraitTabContainer}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v as string)}>
            <Tabs.Tab label="Kiosk" value="kiosk" iconName="kiosks" />
          </Tabs>
        </Grid>

        {activeTab === 'kiosk' && <DashboardView />}
        <Grid direction="column" className={classes.maintenancePortraitLogoutBlock}>
          <Button width="200px" data-testid="exitbuttonPortrait" sx={{ margin: '0 0 5px' }} onClick={logoutButtonClick}>
            Logout
          </Button>
          <Typography variant="body1" align="center" data-testid="versionNumberNew">
            Version: {packageJSON.version}
          </Typography>
          <Typography variant="body1" align="center">
            &nbsp;&nbsp; &#169;KIS Technologies
          </Typography>
        </Grid>
        {showTimerOverlay && (
          <TimerOverlay
            onResetClick={exitToAttract}
            onActiveClick={handleActiveClick}
            startTime={maintenanceTimerCount}
          />
        )}
        {showTicketStock && kioskHardwareIdOutOfStock && (
          <TicketStockOverlay
            onCancel={() => setShowTicketStock(false)}
            onConfirm={onTicketRestockConfirm}
            kioskHardwareId={kioskHardwareIdOutOfStock}
          />
        )}
      </Grid>
    </>
  );
};

export default MaintenanceView;
